/**
 * DeckSection
 *
 * One collapsible deck row in the decks-first folder view. The header
 * (filename + slide count + folder breadcrumb) renders from the
 * SlideSenseDeck object alone — no slide data needed.
 *
 * Slides are fetched lazily via useSlides({ deckId }) the first time the
 * section is expanded, then cached across re-collapses by react-query's
 * query cache. So toggling a section on/off doesn't refetch.
 */

import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SlideSenseDeck, SlideSenseSlide } from "@/features/slide-sense/types"

import { ChevronDown, Folder, Layers, Loader2, Presentation } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { SlideCard } from "./SlideCard"
import { fetchAllDeckSlides } from "../api"
import { useSlides } from "../hooks/use-slides"

export interface DeckSectionProps {
  deck: SlideSenseDeck
  folderName: string | null
  open: boolean
  onToggleOpen: () => void
  /** Called with the clicked slide plus the deck's slide list — the caller
   *  uses the deck's slides as the navigation context for the preview
   *  modal, so prev/next stays within the same deck. */
  onPreview?: (slide: SlideSenseSlide, deckSlides: SlideSenseSlide[]) => void
  onAdd?: (slide: SlideSenseSlide) => void
  onAddDeck?: (slides: SlideSenseSlide[]) => void
}

const GRID_CLASSES =
  "xs:grid-cols-2 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"

const NEXT_PAGE_SKELETON_COUNT = 10

function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let parent = el?.parentElement ?? null
  while (parent) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === "auto" || overflowY === "scroll") return parent
    parent = parent.parentElement
  }
  return null
}

export function DeckSection({
  deck,
  folderName,
  open,
  onToggleOpen,
  onPreview,
  onAdd,
  onAddDeck,
}: DeckSectionProps) {
  const { t } = useTranslation()
  const [isAddingDeck, setIsAddingDeck] = useState(false)

  // Lazy: query stays disabled until the section is opened at least once.
  // After that react-query caches results, so re-collapses don't refetch.
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSlides({
    deckId: deck.id,
    enabled: open,
  })
  const slides: SlideSenseSlide[] = data?.pages.flat() ?? []

  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    if (!open) return
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(handleIntersect, {
      root: getScrollableAncestor(sentinel),
      rootMargin: "200px",
    })
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [handleIntersect, open, slides.length])

  const handleAddEntireDeck = async () => {
    if (!onAddDeck) return
    setIsAddingDeck(true)
    try {
      // Collapsed rows never fetch slides, and infinite scroll may still have
      // pages left — only reuse the cache once the query has run to completion.
      const hasCompleteCache = Boolean(data) && !hasNextPage
      const deckSlides = hasCompleteCache ? slides : await fetchAllDeckSlides(deck.id)
      onAddDeck(deckSlides)
    } catch {
      toast.error(t("slideSense.failedToLoadSlides"))
    } finally {
      setIsAddingDeck(false)
    }
  }

  const panelId = `deck-panel-${deck.id}`

  return (
    <Collapsible
      asChild
      open={open}
      onOpenChange={(next) => {
        if (next !== open) onToggleOpen()
      }}
    >
      <section
        aria-label={deck.original_filename}
        className="mt-2 mb-8 border-t pt-6 first:mt-0 first:border-t-0 first:pt-0"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <button
            aria-controls={panelId}
            aria-expanded={open}
            className="hover:bg-muted/50 -ml-1 flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-0.5 text-left transition-colors"
            type="button"
            aria-label={t(open ? "slideSense.collapseDeck" : "slideSense.expandDeck", {
              name: deck.original_filename,
            })}
            onClick={onToggleOpen}
          >
            <ChevronDown
              className={cn(
                "text-foreground/70 h-4 w-4 shrink-0 transition-transform duration-150",
                !open && "-rotate-90"
              )}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              {folderName && (
                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  <Folder className="h-3.5 w-3.5" />
                  <span>{folderName}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Presentation className="text-primary h-4 w-4 shrink-0" />
                <h2 className="text-foreground truncate text-base font-semibold tracking-tight">
                  {deck.original_filename}
                </h2>
                <span className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs tabular-nums">
                  {t("slideSense.slideCount", { count: deck.slide_count })}
                </span>
                {deck.processing_status === "failed" && (
                  <span
                    className="bg-destructive/10 text-destructive rounded-md px-2 py-0.5 text-xs"
                    data-testid="deck-failed-badge"
                    title={deck.processing_error ?? undefined}
                  >
                    {t("slideSense.renderFailed")}
                  </span>
                )}
              </div>
            </div>
          </button>

          {onAddDeck && deck.slide_count > 0 && (
            <Button
              className="shrink-0 gap-1.5"
              disabled={isAddingDeck}
              size="sm"
              variant="default"
              onClick={() => void handleAddEntireDeck()}
            >
              {isAddingDeck ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Layers className="h-4 w-4" />
              )}
              {t("slideSense.addEntireDeck")}
            </Button>
          )}
        </div>

        <CollapsibleContent>
          <div className={GRID_CLASSES} id={panelId}>
            {isLoading && slides.length === 0
              ? Array.from({ length: Math.min(deck.slide_count, 10) }).map((_, i) => (
                  <DeckSlideSkeleton key={i} />
                ))
              : slides.map((slide) => (
                  <SlideCard
                    key={slide.id}
                    slide={slide}
                    onAdd={onAdd}
                    onPreview={onPreview ? (s) => onPreview(s, slides) : undefined}
                  />
                ))}
            {isFetchingNextPage &&
              Array.from({ length: NEXT_PAGE_SKELETON_COUNT }).map((_, i) => (
                <DeckSlideSkeleton key={`next-skel-${i}`} />
              ))}
          </div>

          <div ref={sentinelRef} className="h-1" />

          {!hasNextPage && slides.length >= 20 && slides.length === deck.slide_count && (
            <p className="text-muted-foreground py-4 text-center text-xs">
              {t("slideSense.allSlidesLoaded", { count: slides.length })}
            </p>
          )}
        </CollapsibleContent>
      </section>
    </Collapsible>
  )
}

function DeckSlideSkeleton() {
  return (
    <div className="border-border/45 overflow-hidden rounded-lg border">
      <Skeleton className="aspect-[16/9] w-full" />
      <div className="space-y-2 p-2.5">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  )
}
